import type { ReactNode } from 'react';
import { AlertTriangle, Ban, CheckCircle2, CircleDashed, Clock, RotateCcw, ShieldCheck, ShieldQuestion, ShieldX, Zap } from 'lucide-react';
import { APPROVAL_STATUS_LABEL, ENTRY_STATUS_LABEL, PRIORITY_LABEL, type ApprovalStatus, type EntryKind, type EntryStatus, type Priority } from '@zuri/shared';
import { cx } from '../../lib/format';

export type Tone = 'neutral' | 'green' | 'red' | 'orange' | 'blue' | 'violet';

const tones: Record<Tone, string> = {
  neutral: 'bg-active text-ink-2',
  green: 'bg-success-soft text-success',
  red: 'bg-danger-soft text-danger',
  orange: 'bg-warning-soft text-warning',
  blue: 'bg-info-soft text-info',
  violet: 'bg-violet-soft text-violet',
};

export function Badge({ tone = 'neutral', icon, children, className, title }: { tone?: Tone; icon?: ReactNode; children: ReactNode; className?: string; title?: string }) {
  return (
    <span title={title} className={cx('inline-flex h-[22px] max-w-full items-center gap-1 whitespace-nowrap rounded-full px-2 text-[12px] font-medium leading-none', tones[tone], className)}>
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
}

const ic = 'size-3.5 shrink-0';

export function EntryStatusBadge({ kind, status }: { kind: EntryKind; status: EntryStatus }) {
  const map: Record<EntryStatus, { tone: Tone; icon: ReactNode }> = {
    open: { tone: 'neutral', icon: <CircleDashed className={ic} /> },
    partial: { tone: 'blue', icon: <Clock className={ic} /> },
    settled: { tone: 'green', icon: <CheckCircle2 className={ic} /> },
    canceled: { tone: 'neutral', icon: <Ban className={ic} /> },
  };
  return <Badge tone={map[status].tone} icon={map[status].icon}>{ENTRY_STATUS_LABEL[kind][status]}</Badge>;
}

export function ApprovalBadge({ status, compact }: { status: ApprovalStatus; compact?: boolean }) {
  const map: Record<ApprovalStatus, { tone: Tone; icon: ReactNode; short: string }> = {
    not_requested: { tone: 'neutral', icon: <ShieldQuestion className={ic} />, short: 'Sem aprovação' },
    pending: { tone: 'orange', icon: <Clock className={ic} />, short: 'Aguardando' },
    approved: { tone: 'green', icon: <ShieldCheck className={ic} />, short: 'Autorizado' },
    rejected: { tone: 'red', icon: <ShieldX className={ic} />, short: 'Recusado' },
    invalidated: { tone: 'violet', icon: <RotateCcw className={ic} />, short: 'Reaprovar' },
  };
  const m = map[status];
  return <Badge tone={m.tone} icon={m.icon} title={APPROVAL_STATUS_LABEL[status]}>{compact ? m.short : APPROVAL_STATUS_LABEL[status]}</Badge>;
}

export function OverdueBadge() {
  return <Badge tone="red" icon={<AlertTriangle className={ic} />}>Vencido</Badge>;
}

export function PriorityBadge({ priority }: { priority: Priority }) {
  if (priority === 'normal' || priority === 'low') return null;
  return <Badge tone={priority === 'urgent' ? 'red' : 'orange'} icon={<Zap className={ic} />}>{PRIORITY_LABEL[priority]}</Badge>;
}

export function MissingAmountBadge() {
  return <Badge tone="orange" icon={<AlertTriangle className={ic} />}>Valor a informar</Badge>;
}
